import { CardAffinity, CardType } from '@mythos/game/CardTypes';
import { Phase } from '@mythos/game/Phases';

export const ColorPalette = {
  printBlack: '#000000ff',

  // Symbol Colors
  derionGreen: 'rgb(142, 200, 153)', // '#21a360',
  derionGreen2: 'rgb(77, 179, 118)',
  zeonaBlue: 'rgb(69, 148, 226)', // '#408dcb',
  tyrosRed: 'rgb(212, 52, 34)', // '#c52a15',
  unitBronze: 'rgb(201, 158, 99)', // '#b07e49',
  buildingSilver: 'rgb(147, 166, 182)', // '#c6c6c6',
  eventGold: 'rgb(184, 159, 12)', // '#e9a304',
  favor: 'rgb(213, 209, 229)',

  missingColor: 'pink',

  // Text Colors
  brightText: 'rgb(215, 215, 215)',
  normalText: 'rgb(194, 194, 194)',
  dimText: 'rgb(130, 130, 130)',

  // BG Colors
  darkPurpleBG: 'rgb(42, 41, 49)',
  darkerPurpleBG: 'rgb(37, 37, 46)',
  lightBG: 'rgb(60, 61, 66)',
};

export const PlayerColors = [
  'rgb(32, 123, 234)',
  'rgb(153, 105, 250)',
  'rgb(224, 139, 20)',
  'rgb(29, 170, 41)',
  'rgb(207, 35, 47)',
  'rgb(30, 184, 186)',
  'rgb(70, 172, 226)',
  'rgb(223, 80, 44)',
];

export const PhaseToBackgroundColor: Record<Phase, string> = {
  SETUP: 'rgb(50, 50, 50)',
  PRODUCTION: 'rgb(50, 110, 50)',
  PLANNING: 'rgb(36, 49, 68)',
  WAR: 'rgb(117, 45, 15)',
  RESOLUTION: 'rgb(55, 43, 83)',
  TRIBUTE: 'rgb(70, 50, 120)',
};

export const CardAffinityToFrameColor: Record<CardAffinity, string> = {
  [CardAffinity.Basic]: 'rgb(150, 111, 93)',
  [CardAffinity.Derion]: 'rgb(142, 200, 153)',
  [CardAffinity.Tyros]: 'rgb(172, 87, 59)',
  [CardAffinity.Zeona]: 'rgb(104, 172, 223)',
  [CardAffinity.Leader]: 'rgb(199, 199, 199)',
  [CardAffinity.None]: 'rgb(110, 110, 110)',
};

export const CardTypeToSymbolColor: Record<CardType, string> = {
  [CardType.Unit]: ColorPalette.unitBronze,
  [CardType.Building]: ColorPalette.buildingSilver,
  [CardType.Event]: ColorPalette.eventGold,
};

export const CardAffinityToCardCountBGColor: Record<CardAffinity, string> = {
  [CardAffinity.Basic]: 'rgb(162, 93, 71)',
  [CardAffinity.Derion]: ColorPalette.derionGreen2,
  [CardAffinity.Tyros]: ColorPalette.tyrosRed,
  [CardAffinity.Zeona]: ColorPalette.zeonaBlue,
  [CardAffinity.Leader]: ColorPalette.missingColor,
  [CardAffinity.None]: ColorPalette.missingColor,
};

export const CardTypeToCardCountBGColor: Record<CardType, string> = {
  [CardType.Unit]: ColorPalette.unitBronze,
  [CardType.Building]: ColorPalette.buildingSilver,
  [CardType.Event]: ColorPalette.eventGold,
};

export const UIColorPalette = {
  // Menu Colors
  lobbyDarkBG: 'rgb(99, 85, 128)',
  lobbyDarkerBG: 'rgb(66, 54, 95)',
  lobbyDarkText: ColorPalette.brightText,
  lobbyDarkBorder: 'rgb(85, 85, 85)',
  lobbyDarkBorderLight: 'rgb(110, 110, 110)',
  lobbyDarkBorderBottom: 'rgb(90, 90, 90)',

  // Lobby Page Colors
  lobbyPlayerListBg: 'rgb(47, 44, 54)',
  lobbyTooltipBg: 'rgb(67, 67, 67)',
  lobbyListEntryDateText: 'rgb(150, 150, 150)',

  // Login Page Colors
  loginPageDescriptionText: 'rgb(0, 0, 0)',
  loginDiscordButtonBG: 'rgb(211, 211, 211)',

  // Button Colors
  buttonPrimaryText: 'rgb(0, 0, 0)',
  buttonPrimary: 'rgb(227, 198, 101)',
  buttonPrimaryBorder: 'rgb(177, 148, 51)',
  buttonPrimaryHover: 'rgb(217, 188, 91)',
  buttonPrimaryHoverBorder: 'rgb(167, 138, 41)',
  buttonPrimaryActive: 'rgb(207, 178, 71)',
  buttonPrimaryActiveBorder: 'rgb(157, 128, 31)',
  buttonSecondaryText: 'rgb(0, 0, 0)',
  buttonSecondary: 'rgb(195, 195, 195)',
  buttonSecondaryBorder: 'rgb(150, 150, 150)',
  buttonSecondaryHover: 'rgb(185, 185, 185)',
  buttonSecondaryHoverBorder: 'rgb(140, 140, 140)',
  buttonSecondaryActive: 'rgb(175, 175, 175)',
  buttonSecondaryActiveBorder: 'rgb(130, 130, 130)',
  buttonSecondaryDisabled: 'rgba(19, 1, 1, 0.3)',
  mobileHoverButtonBG: 'rgba(154, 143, 219, 1)',

  // Card colors (Move out of this object)
  effectPillXText: ColorPalette.printBlack,
  symbolWithNumberText: ColorPalette.printBlack,

  // Self player view submit button colors
  statusButtonRed: 'rgb(255, 81, 28)',
  statusButtonBorder: 'rgba(255, 255, 255, 0.2)',
  statusButtonBorderDark: 'rgba(0, 0, 0, 0.2)',
  statusButtonDisabled: 'rgb(140, 140, 140)',
  statusButtonGreen: 'rgb(56, 159, 80)',

  playerSummaryBG: 'rgb(69, 69, 81)',
  mPlayerInfoBG: 'rgb(69, 69, 81)',
  mPlayerInfoBG2: 'rgb(81, 51, 108)',

  selfPlayerViewBG: 'rgb(210, 210, 210, .45)',

  cantSubmitText: 'rgb(135, 135, 135)',
  cantSubmitBG: 'rgb(0, 0, 0)',
  mSelfPlayerGoldBG: 'rgb(150, 150, 150)',
  mSelfPlayerGoldText: 'rgb(50, 50, 50)',

  conflictResultsBG: 'rgba(210, 210, 210, 0.9)',
  conflictResultsLoserBG: 'rgba(0, 0, 0, 0.5)',
  allCardsPileBG: 'rgba(37, 37, 46)',
  allCardsPileText: 'rgb(206, 198, 189)',

  playerSummaryWinnerBG: 'rgb(81, 51, 108)',
  playerSummaryResourceText: ColorPalette.brightText,
  playerSummaryResourceTextMaxed: 'rgb(255, 100, 100)',
  playerSummaryResourceTextNearMaxed: 'rgb(255, 170, 120)',
  playerSummaryResourceTextLight: 'rgba(255, 238, 210, 0.5)',

  unaffordableCardBannerBG: 'rgba(150, 0, 0, 0.8)',
  unaffordableCardText: ColorPalette.brightText,
  disallowedCardOverlay: 'rgba(0, 0, 0, 0.5)',

  // Log View Colors
  logBG: 'rgb(39, 43, 57)',
  logItemBackground: ColorPalette.lightBG,
  logItemTextColor: ColorPalette.normalText,

  tutorialButtonBorder: 'rgb(117, 119, 123)',
  tutorialButtonShadow: 'rgba(0, 0, 0, 0.2)',
  tutorialButtonBorderDisabled: 'rgb(70, 70, 70)',
  tutorialButtonDisabled: 'rgba(32, 28, 32, 1)',

  logDividerBackground: 'rgb(61, 76, 105)',
  logTurnOneDividerBackground: 'rgb(81, 94, 119)',

  logBorderWar: 'rgb(192, 108, 75)',
  logBorderFavor: 'rgb(99, 78, 201)',
  logBorderGold: 'rgb(198, 161, 67)',
  logBorderProduction: 'rgb(133, 99, 57)', //'rgb(77, 151, 67)',
  logBorderNeutral: 'rgb(123, 123, 123)',
  logBorderChat: 'rgb(82, 140, 182)',
  // conflict results event
  conflictResultsRoundDivider: 'rgb(160, 160, 160)',

  mPlayerSummaryText: ColorPalette.brightText,
  gameStateText: ColorPalette.brightText,
  gameEndText: ColorPalette.brightText,
  gamePileTitleText: ColorPalette.brightText,
  playerNameText: ColorPalette.brightText,

  cardCountText: ColorPalette.normalText,

  selectedCardHighlight: 'white',
  completedTributeHighlight: 'rgb(180, 72, 247)',

  // Background Colors
  backgroundWhite: 'white',
  modalOverlayBackdrop: 'rgba(0, 0, 0, 0.5)',

  playerBoardHeaderBG: ColorPalette.darkPurpleBG,
  playerBoardBG: ColorPalette.darkerPurpleBG,

  mPlayerDetailsSectionBG: 'rgba(0, 0, 0, 0.3)',
  mPlayerDetailsUpgradeContainerBG: 'rgba(255, 255, 255, 0.3)',
  pileCardHoverOverlay: 'rgba(0, 0, 0, 0.4)',
  mPileContainerBG: 'rgba(255, 255, 255, 0.1)',

  cardDebugPageTextAreaBG: 'white',

  // Status/Indicator Colors
  playerSummaryStatusReady: '#00a058',
  playerSummaryStatusNotReady: 'rgb(241, 83, 34)',

  cardCountProphecy: 'rgb(97, 9, 230)',
  cardCountProphecyBorder: 'white',

  chatOverlayBG: 'rgb(39, 43, 57)',
  chatOverlayBorder: 'rgb(200, 200, 200)',

  mPlayerDetailsBG: 'rgb(50, 50, 50)',
  mPlayerDetailsUpgradeBG: 'rgba(31, 31, 31, 1)',

  // Page Header Colors
  pageHeaderBg: 'rgb(80, 72, 87)',
  userMenuBg: 'rgb(80, 77, 83)',
  pageHeaderLink: 'rgb(220, 200, 130)',
  pageHeaderLinkHover: 'rgb(240, 220, 150)',
  pageHeaderUserHover: 'rgb(100, 100, 100)',
  pageHeaderLinkActive: 'rgb(255, 255, 255)',

  // End of Game Colors
  eogPieChartText: 'white',
  endGameCardBasic: 'rgb(128,98,50)',
  endGameCardDerion: 'rgb(55,150,95)',
  endGameCardTyros: 'rgb(193,105,60)',
  endGameCardZeona: 'rgb(70,130,195)',
  endGameTribute0: 'rgb(52, 56, 74)',
  endGameTribute1: 'rgb(150, 110, 180)',
  endGameTribute2: 'rgb(120, 80, 180)',
  endGameTribute3: 'rgb(110, 60, 180)',
  eogConflictResultsTitleText: 'rgb(0, 0, 0)',
  eogConflictResultsWinnerName: 'rgb(225, 95, 50)',
  eogConflictResultsPlayerName: 'rgb(0, 0, 0)',

  // Game History Colors
  gameHistoryItemSeparator: 'rgb(200, 200, 200)',
  gameHistoryItemBg: 'rgb(200, 200, 200)',

  // Rules Page Colors
  rulesBg: 'rgb(36, 49, 68)',
  rulesText: ColorPalette.brightText,
  rulesLink: 'rgb(228, 169, 102)',
  rulesLinkHash: 'rgb(170, 170, 170)', //'#aaa',
  rulesTableBgDark: 'rgb(156, 158, 169,.8)',

  // Card Name Colors
  cardNameLink: 'rgb(113, 132, 232)',
  cardNameLinkHover: 'rgb(150, 167, 255)',

  // Collapsible Links Colors
  collapsibleBg: 'rgb(91, 91, 91)',
  collapsibleText: 'rgb(254, 198, 10)',
  collapsibleHeaderBg: 'rgb(74, 74, 74)',
  collapsibleBorder: 'rgb(151, 151, 151)',

  // Additional Colors
  discordBlue: 'rgb(98, 101, 242)',
  listItemBottomBorder: 'rgb(76, 80, 98)',
  listBackground: 'rgb(60, 61, 66)',

  // Per Age Pill Colors
  perAgePillBackground: 'rgb(142, 142, 142)',
  perAgePillGradient:
    'linear-gradient(110deg, rgba(142,142,142,1) 0%, rgba(142,142,142,1) 13.5px, rgba(116,116,116,1) 13.5px, rgba(116,116,116,1) 67%, rgba(85,85,85,1) 67%, rgba(87,87,87,1) 100%)',
  perAgePillHighlight: 'rgb(227, 187, 53)',

  leaderboardAccountAgeText: 'rgb(122, 122, 122)',
  leaderboardSubtitleText: 'rgb(168, 151, 130)',
  leaderboardGameWinsText: 'rgb(185, 169, 129)',
  // Medal/Rank Colors
  leaderboardPositionText: 'rgb(102, 102, 102)',
  leaderboardGold: 'rgb(212, 175, 55)',
  leaderboardSilver: 'rgb(192, 192, 192)',
  leaderboardBronze: 'rgb(205, 127, 50)',

  ratingBadgeBorder: 'rgb(156, 136, 101)',
  ratingBadgeBoxShadow: 'rgba(0, 0, 0, 0.06)',
  ratingBadgeText: 'rgb(232, 189, 115)',
  ratingBadgeDeltaText: 'rgb(170, 170, 170)',
};
